"use client";

/**
 * The account billing section (Plan 02 Phase 3 / D3 wave 1): sak's storage
 * meters — the byte ledger against the plan threshold and the per-file cap —
 * rendered by the shared kit (`@suite/billing`) from the entitlement
 * snapshots. The over-limit banner and its top-up CTA ride the kit's
 * `PaywallCard` → `UpgradeSheet` round trip; sak itself never derives a limit
 * (the D3 rule: apps consume snapshots, the server stays the enforcement
 * point). Replaces the retired legacy quota-tier list (the hub's
 * `/billing/plans` answers an empty catalog since the Phase 0 legacy guard).
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BillingLabelsProvider,
  EN_DEFAULTS,
  PaywallCard,
  UpgradeSheet,
  UsageMeter,
  meterLabel,
  usePaywall,
  type BillingLabels,
  type Plan,
} from "@suite/billing";
import "@suite/billing/theme/billing.css";

import { sakBillingLabels } from "@/lib/billingLabels";

export function BillingSection() {
  const { t } = useTranslation();
  const billingLabels = useMemo<BillingLabels>(() => ({ ...EN_DEFAULTS, ...sakBillingLabels(t) }), [t]);
  const paywall = usePaywall("storage");
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);

  // The settings surface is the account's authoritative billing view: the
  // meters re-read fresh on every mount instead of serving the 5-minute
  // shared cache (the Phase 2 rule the hub's section follows too).
  useEffect(() => {
    void paywall.refresh();
    // Mount-only: the hook's own state drives every later re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BillingLabelsProvider labels={billingLabels}>
      <section className="suite-billing-section" aria-label={billingLabels.billingSectionTitle}>
        <div className="suite-billing-card">
          <div className="suite-billing-card-head">
            <span className="suite-billing-card-title">{billingLabels.serviceStorage}</span>
          </div>

          {paywall.loading && paywall.meters.length === 0 && (
            <div className="suite-billing-skeleton suite-billing-skeleton--meter" />
          )}

          {paywall.error && paywall.meters.length === 0 && (
            <div className="suite-billing-error">
              <span>{paywall.error}</span>
              <button type="button" className="suite-billing-button" onClick={() => void paywall.refresh()}>
                {billingLabels.retry}
              </button>
            </div>
          )}

          {!paywall.loading && !paywall.error && paywall.meters.length === 0 && (
            <p className="suite-billing-sheet-subtitle">{billingLabels.billingSectionEmpty}</p>
          )}

          {paywall.meters.map((meter) => (
            <UsageMeter key={meter.key} label={meterLabel(billingLabels, meter.key)} meter={meter} />
          ))}

          {paywall.blocked && (
            <PaywallCard
              service={billingLabels.serviceStorage}
              variant="inline-banner"
              onUpgrade={paywall.pack ? () => setCheckoutPlan(paywall.pack) : undefined}
            />
          )}
        </div>
      </section>
      <UpgradeSheet
        open={checkoutPlan !== null}
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
        onSuccess={() => void paywall.refresh()}
      />
    </BillingLabelsProvider>
  );
}
